import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Widget from "Components/Common/Widget/Widget";
import { MdBarChart, MdWifiCalling3 } from "react-icons/md";
import TotalSpent from "./components/TotalSpent";
import WeeklyRevenue from "./components/WeeklyRevenue";
import welcomeBg from "../../../assets/img/dashboards/welcomeBg.png";
import {
  FaUsers,
  FaCoins,
  FaMoneyBillWave,
  FaPhoneVolume,
  FaVideo,
  FaExchangeAlt,
  FaUserFriends,
} from "react-icons/fa";
import { HiPhoneMissedCall } from "react-icons/hi";
import { RiUserReceived2Fill } from "react-icons/ri";
import { IoMdMale, IoMdFemale } from "react-icons/io";
import { FaFilter } from "react-icons/fa6";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Dashboard = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    transactionStats: {},
    userStats: {},
    withdrawalStats: {},
    callStats: {},
    giftStats: {},
    otherStats: {},
  });

  const [filterType, setFilterType] = useState("today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // const fetchData = useCallback(async () => {
  //   try {
  //     setLoading(true);

  //     let params = { filterType };

  //     if (filterType === "custom" && fromDate && toDate) {
  //       params.startDate = fromDate.toString().split("T")[0];
  //       params.endDate = toDate.toString().split("T")[0];
  //     }

  //     const response = await axios.get(
  //       `${process.env.REACT_APP_API_URL}/admin/stats`,
  //       { params }
  //     );

  //     setStats(response?.data || {});
  //   } catch (err) {
  //     console.error("Dashboard data fetch error:", err);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [filterType, fromDate, toDate]);

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const params = { filterType };

      if (filterType === "custom" && fromDate && toDate) {
        params.startDate = formatLocalDate(fromDate);
        params.endDate = formatLocalDate(toDate);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/stats`,
        { params }
      );

      setStats(response?.data || {});
    } catch (err) {
      console.error("Dashboard data fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [filterType, fromDate, toDate]);

  useEffect(() => {
    if (filterType === "custom" && (!fromDate || !toDate)) return;
    fetchData();
  }, [filterType, fromDate, toDate, location.key]);

  const SkeletonBox = ({ className }) => (
    <div className={`animate-pulse rounded-lg bg-gray-300 ${className}`} />
  );

  if (loading) {
    return (
      <div className="p-4">
        <SkeletonBox className="mb-6 h-40 w-full" />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-6">
          {Array(6)
            .fill(0)
            .map((_, i) => (
              <SkeletonBox key={i} className="h-28 w-full" />
            ))}
        </div>

        <h2 className="mb-4 mt-8 text-xl font-bold text-gray-600">
          User Statistics
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-6">
          {Array(6)
            .fill(0)
            .map((_, i) => (
              <SkeletonBox key={i} className="h-28 w-full" />
            ))}
        </div>

        <h2 className="mb-4 mt-8 text-xl font-bold text-gray-600">
          Transaction Statistics
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-6">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <SkeletonBox key={i} className="h-28 w-full" />
            ))}
        </div>

        <h2 className="mb-4 mt-8 text-xl font-bold text-gray-600">
          Withdrawal Statistics
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-6">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <SkeletonBox key={i} className="h-28 w-full" />
            ))}
        </div>

        <h2 className="mb-4 mt-8 text-xl font-bold text-gray-600">
          Call Statistics
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-6">
          {Array(2)
            .fill(0)
            .map((_, i) => (
              <SkeletonBox key={i} className="h-28 w-full" />
            ))}
        </div>

        <h2 className="mb-4 mt-8 text-xl font-bold text-gray-600">
          Gift Statistics
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-6">
          {Array(2)
            .fill(0)
            .map((_, i) => (
              <SkeletonBox key={i} className="h-28 w-full" />
            ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          <SkeletonBox className="h-72 w-full" />
          <SkeletonBox className="h-72 w-full" />
        </div>
      </div>
    );
  }
  return (
    <React.Fragment>
      {/* Welcome Banner */}
      <div
        className="mb-5 mt-3 rounded-xl p-4 shadow-sm"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="my-8 text-center">
          <h1
            style={{ fontFamily: "cursive" }}
            className="text-3xl font-bold tracking-wider text-[#eee7ea] md:text-4xl"
          >
            Welcome Back, Admin!
          </h1>
          <p className="text-md mt-4 font-semibold tracking-wide text-navy-800 md:text-lg">
            Empowering You to Manage and Optimize Effortlessly
          </p>
        </div>
      </div>

      {/* Filter Section */}
      <div className="mb-6 flex flex-wrap items-center justify-end gap-4">
        {/* Filter Dropdown with Icon */}
        <div className="relative">
          <FaFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
          <select
            value={filterType}
            onChange={(e) => {
              const val = e.target.value;
              setFilterType(val);

              if (val === "custom") {
                setFromDate(null);
                setToDate(null);
              }
            }}
            className="rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-8 text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="today">Today</option>
            <option value="all">All</option>
            <option value="last7days">Last 7 Days</option>
            <option value="custom">Custom Date</option>
          </select>
        </div>

        {/* Custom Date Pickers */}
        {filterType === "custom" && (
          <div className="flex flex-wrap items-center gap-3">
            <DatePicker
              selected={fromDate}
              onChange={(date) => setFromDate(date)}
              selectsStart
              startDate={fromDate}
              endDate={toDate}
              placeholderText="From Date"
              dateFormat="dd/MM/yyyy"
              className="rounded-lg border border-gray-300 p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <DatePicker
              selected={toDate}
              onChange={(date) => setToDate(date)}
              selectsEnd
              startDate={fromDate}
              endDate={toDate}
              minDate={fromDate}
              placeholderText="To Date"
              dateFormat="dd/MM/yyyy"
              className="rounded-lg border border-gray-300 p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
      </div>

      {/* Top Stats */}
      <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-6">
        <Widget
          icon={<MdBarChart className="h-6 w-6" />}
          title={"Earnings"}
          subtitle={stats.otherStats.earnings}
        />
        <Widget
          icon={<FaUsers className="h-6 w-6" />}
          title={"Our Total Hearts"}
          subtitle={stats.otherStats.ourTotalHearts}
        />
        <Widget
          icon={<FaUsers className="h-6 w-6" />}
          title={"Group Of Hearts"}
          subtitle={stats.otherStats.groupOfHearts}
        />
        <Widget
          icon={<MdWifiCalling3 className="h-6 w-6" />}
          title={"Engaged Hearts (In Call)"}
          subtitle={stats.otherStats.engagedHearts}
        />
        <Widget
          icon={<HiPhoneMissedCall className="h-6 w-6" />}
          title={"Idle Hearts (In Call)"}
          subtitle={stats.otherStats.idleHearts}
        />
        <Widget
          icon={<RiUserReceived2Fill className="h-6 w-6" />}
          title={"Newly Joined "}
          subtitle={stats.otherStats.newlyJoined}
        />
      </div>

      {/* User Stats */}
      <div className="mt-8">
        <h2 className="mb-4 text-xl font-bold text-gray-800">
          User Statistics
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-6">
          <Widget
            icon={<FaUsers className="h-6 w-6" />}
            title={"Total Users"}
            subtitle={stats.userStats.totalUsers}
          />
          <Widget
            icon={<IoMdMale className="h-6 w-6 text-blue-500" />}
            title={"Male Users"}
            subtitle={stats.userStats.maleUsers}
          />
          <Widget
            icon={<IoMdFemale className="h-6 w-6 text-pink-500" />}
            title={"Female Users"}
            subtitle={stats.userStats.femaleUsers}
          />
          <Widget
            icon={<RiUserReceived2Fill className="h-6 w-6" />}
            title={"New Users"}
            subtitle={stats.userStats.newUsers}
          />
          <Widget
            icon={<FaUserFriends className="h-6 w-6" />}
            title={"Users in Calls"}
            subtitle={stats.userStats.usersInCalls}
          />
          <Widget
            icon={<FaUserFriends className="h-6 w-6 text-pink-500" />}
            title={"Female in Calls"}
            subtitle={stats.userStats.femaleInCalls}
          />
        </div>
      </div>

      {/* Transaction Stats */}
      <div className="mt-8">
        <h2 className="mb-4 text-xl font-bold text-gray-800">
          Transaction Statistics
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-6">
          <Widget
            icon={<FaMoneyBillWave className="h-6 w-6 text-green-500" />}
            title={"Total Transactions"}
            subtitle={stats.transactionStats.totalTransactions}
          />
          <Widget
            icon={<FaMoneyBillWave className="h-6 w-6 text-green-500" />}
            title={"Total Amount"}
            subtitle={`₹${stats.transactionStats.totalAmount}`}
          />
          <Widget
            icon={<FaCoins className="h-6 w-6 text-yellow-500" />}
            title={"Coins Sold"}
            subtitle={stats.transactionStats.coinsSold}
          />
          <Widget
            icon={<FaCoins className="h-6 w-6 text-orange-500" />}
            title={"Coins Used"}
            subtitle={stats.transactionStats.coinsUsed}
          />
        </div>
      </div>

      {/* Withdrawal Stats */}
      <div className="mt-8">
        <h2 className="mb-4 text-xl font-bold text-gray-800">
          Withdrawal Statistics
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-6">
          <Widget
            icon={<FaExchangeAlt className="h-6 w-6 text-red-500" />}
            title={"Withdrawal Requests"}
            subtitle={stats.withdrawalStats.withdrawalRequests}
          />
          <Widget
            icon={<FaMoneyBillWave className="h-6 w-6 text-blue-500" />}
            title={"Requested Amount"}
            subtitle={`₹${stats.withdrawalStats.requestedAmount}`}
          />
          <Widget
            icon={<FaMoneyBillWave className="h-6 w-6 text-green-500" />}
            title={"Successful Amount"}
            subtitle={`₹${stats.withdrawalStats.successfulAmount}`}
          />
          <Widget
            icon={<FaMoneyBillWave className="h-6 w-6 text-gray-500" />}
            title={"Balance Amount"}
            subtitle={`₹${stats.withdrawalStats.balanceAmount}`}
          />
        </div>
      </div>

      {/* Call Stats */}
      <div className="mt-8">
        <h2 className="mb-4 text-xl font-bold text-gray-800">
          Call Statistics
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-6">
          <Widget
            icon={<FaPhoneVolume className="h-6 w-6 text-blue-500" />}
            title={"Audio (in hours)"}
            subtitle={stats.callStats.audioHours}
          />
          <Widget
            icon={<FaVideo className="h-6 w-6 text-purple-500" />}
            title={"Video (in hours)"}
            subtitle={stats.callStats.videoHours}
          />
        </div>
      </div>

      {/* Gift Stats */}
      <div className="mt-8">
        <h2 className="mb-4 text-xl font-bold text-gray-800">
          Gift Statistics
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-6">
          <Widget
            icon={<FaCoins className="h-6 w-6 text-yellow-500" />}
            title={"Group Gifts"}
            subtitle={stats.giftStats.groupGifts}
          />
          <Widget
            icon={<FaCoins className="h-6 w-6 text-yellow-500" />}
            title={"Call Gifts"}
            subtitle={stats.giftStats.callGifts}
          />
        </div>
      </div>

      {/* Charts */}
      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <TotalSpent
          engagedHearts={stats.otherStats.engagedHearts}
          idleHearts={stats.otherStats.idleHearts}
        />

        <WeeklyRevenue />
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
